import type { BuildInfo } from '@oip/contracts';

/**
 * What this bundle was built from.
 *
 * Compiled in by Vite (`define`), because the browser holds whatever it last
 * downloaded and nothing at runtime can tell it which build that was. Asking the
 * API instead would answer a different question — the API is a separate
 * deployable, and the gap between the two is the interesting case.
 *
 * That gap is not hypothetical. Twice while building this platform a stale
 * bundle survived a rebuild, and once an API process kept serving pre-change
 * code after what looked like a restart. Both presented as "the feature does not
 * work" and cost real time.
 */
declare const __OIP_BUILD__: {
  release: string | null;
  commit: string | null;
  branch: string | null;
  builtAt: string | null;
};

export const WEB_BUILD: BuildInfo = {
  release: __OIP_BUILD__?.release ?? null,
  commit: __OIP_BUILD__?.commit ?? null,
  branch: __OIP_BUILD__?.branch ?? null,
  builtAt: __OIP_BUILD__?.builtAt ?? null,
  unstamped: !__OIP_BUILD__?.commit,
};

/**
 * The version somebody reads, quotes and compares.
 *
 * `git describe` output is precise but wordy — `v1.4.0-95-g0416eb2` carries
 * three facts in one string. Shown as **`v1.4.0+95`**: the release it descends
 * from and how far past it, which is the part a person can hold in their head
 * and rank against another. The commit is one hover away and unabridged, since
 * that is what an engineer actually checks out.
 *
 * A bare commit is precise and unreadable: nobody can tell whether `0416eb2`
 * comes before or after `7bda2b6`, and that ordering is usually the question.
 */
export function describeBuild(b: BuildInfo): string {
  if (b.release) return shortRelease(b.release);
  // No tag reachable — the commit is all the identity there is, and it beats
  // inventing a number that would not order against anything.
  if (b.commit) return b.commit;
  return 'unstamped build';
}

/** `v1.4.0-95-g0416eb2` → `v1.4.0+95`. Exact tags and `-dirty` are preserved. */
export function shortRelease(release: string): string {
  const dirty = release.endsWith('-dirty');
  const core = dirty ? release.slice(0, -'-dirty'.length) : release;
  // tag-N-gSHA. Anchored, because a tag may legitimately contain a hyphen
  // ("v2.0.0-rc1") and a greedy match would mangle it.
  const m = /^(.*)-(\d+)-g[0-9a-f]+$/.exec(core);
  const label = m ? `${m[1]}+${m[2]}` : core;
  // Uncommitted changes are part of the identity, not a footnote: a build made
  // over a dirty tree is not the commit it claims to be.
  return dirty ? `${label} (modified)` : label;
}

/**
 * Whether the page and the API came from the same commit.
 *
 * `null` when either side is unstamped — that is "cannot tell", which is a
 * different answer from "they match", and reporting it as agreement is how a
 * genuine mismatch would go unnoticed on exactly the deployments least likely
 * to be stamped.
 */
export function buildsAgree(web: BuildInfo, api: BuildInfo): boolean | null {
  if (!web.commit || !api.commit) return null;
  return web.commit === api.commit;
}
